import { useRef, useState } from 'react'
import { ATTACHMENT_MAX_BYTES, ATTACHMENT_TYPES, type Attachment, type Member } from '@cs/shared'
import { api } from '../api.js'
import { describeError } from '../App.js'

const ACCEPT = Object.keys(ATTACHMENT_TYPES)
  .map((e) => `.${e}`)
  .join(',')

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const IMAGE = /^image\//

/**
 * Shared files.
 *
 * The one place in this system where content crosses the boundary without anything
 * having read it. A .xlsx or a .pdf cannot go through the outbound scan, so each
 * file carries a visible mark saying whether it was checked — the point being that
 * a scanner elsewhere in the product must not be allowed to imply a guarantee here.
 */
export function Files({
  room,
  files,
  me,
  canEdit,
  onChanged,
}: {
  room: string
  files: Attachment[]
  me: Member
  canEdit: boolean
  onChanged: () => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [preview, setPreview] = useState<string | null>(null)

  const upload = async (file: File) => {
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setError(`${file.name} は ${humanSize(file.size)} です。上限は 25MB です。`)
      return
    }
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const saved = await api.uploadFile(room, file, note.trim() || undefined)
      setNote('')
      if (input.current) input.current.value = ''
      setNotice(
        saved.scanned
          ? `${saved.name} を共有しました。内容は検査済みです。`
          : `${saved.name} を共有しました。${'この形式は中身を検査できません'} — 相手にそのまま渡ります。`,
      )
      onChanged()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (file: Attachment) => {
    if (!window.confirm(`${file.name} を取り下げますか？`)) return
    try {
      const res = await api.deleteFile(room, file.id)
      setNotice(
        res.already_downloaded_by.length === 0
          ? `${file.name} を削除しました。まだ誰もダウンロードしていませんでした。`
          : `${file.name} を削除しました。ただし ${res.already_downloaded_by.join(
              '・',
            )} さんは既にダウンロード済みです。手元の控えまでは消せません。`,
      )
      onChanged()
    } catch (err) {
      setError(describeError(err))
    }
  }

  return (
    <div className="card">
      <h2>添付（版管理なし）</h2>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        docx・xlsx・pdf・画像・zip など。履歴は残らず、上書きではなく差し替えになります。
      </p>
      {error && <div className="error">{error}</div>}
      {notice && (
        <div className="notice">
          {notice}
          <button className="text" onClick={() => setNotice(null)}>
            閉じる
          </button>
        </div>
      )}

      {files.length === 0 && (
        <p className="muted">
          まだありません。画面キャプチャ、項目定義の xlsx、PDF の設計書、CSV などをここに
          置けます（1ファイル 25MB まで）。
        </p>
      )}

      {files.map((f) => (
        <div key={f.id} style={{ marginBottom: 12 }}>
          <div className="row" style={{ gap: 8 }}>
            <a href={api.fileUrl(room, f.id)} download={f.name}>
              {f.name}
            </a>
            <span className="muted" style={{ fontSize: 12 }}>
              {humanSize(f.size)}
            </span>
            {f.scanned ? (
              <span className="pill done" title="テキスト形式なので送信前の検査を通しています">
                検査済み
              </span>
            ) : (
              <span className="pill warn" title="この形式は中身を読めないため検査していません">
                未検査
              </span>
            )}
            {!f.published_at && <span className="pill">自分だけに表示中</span>}
            {IMAGE.test(f.content_type) && (
              <button onClick={() => setPreview(preview === f.id ? null : f.id)}>
                {preview === f.id ? '閉じる' : '表示'}
              </button>
            )}
            {f.uploaded_by === me.user_id && (
              <button className="danger" style={{ marginLeft: 'auto' }} onClick={() => remove(f)}>
                取り下げる
              </button>
            )}
          </div>
          {f.note && <div className="muted">{f.note}</div>}
          <div className="muted" style={{ fontSize: 12 }}>
            {f.uploaded_by_name} が {new Date(f.uploaded_at).toLocaleString('ja-JP')} に共有 ・
            SHA-256 {f.sha256.slice(0, 16)}…
          </div>
          {preview === f.id && (
            <img
              src={api.fileUrl(room, f.id)}
              alt={f.name}
              style={{
                maxWidth: '100%',
                marginTop: 8,
                borderRadius: 12,
                border: '1px solid var(--outline-variant)',
              }}
            />
          )}
        </div>
      ))}

      {canEdit && (
        <>
          <hr />
          <label htmlFor="file-note">メモ (任意)</label>
          <input
            id="file-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="例: 項目定義 v3。赤字が今回の変更"
          />
          <div className="row" style={{ marginTop: 8 }}>
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void upload(file)
              }}
              style={{ flex: '1 1 240px' }}
            />
          </div>
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            選んだ時点で相手に共有されます。
            <strong>
              docx・xlsx・pptx・pdf・画像・zip は中身を検査できません。
            </strong>
            認証情報や、相手に渡してはいけない情報が入っていないか、ご自身で確認してから
            選んでください。
          </p>
        </>
      )}
    </div>
  )
}
